import {
  DropdownList,
  type DropdownListGroup,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";
import type { VariableSelectorProps } from "./types";
import { useEffect, useMemo, useState } from "react";
import { nodeVariableToGroups } from "../types";
import { cn } from "@/utils/theme";

export const VariableSelector = ({
  variables,
  onSelectVariable,
  triggerGroup: group,
  selectedVariable,
  placeholder,
  className,
  isLoading,
  open,
  onOpenChange,
}: VariableSelectorProps) => {
  const [_selectedVariable, setSelectedVariable] = useState<
    IDropdownListItem | undefined
  >(() => {
    if (!selectedVariable) {
      return undefined;
    }

    const groups = nodeVariableToGroups(variables);
    const foundGroup = groups.find(
      (g) => g.groupId === selectedVariable.node_id,
    );
    const variable = foundGroup?.items.find((i) => {
      return i.value === selectedVariable.id;
    });

    if (!variable) {
      return undefined;
    }

    return variable;
  });
  const [_selectedGroup, setSelectedGroup] = useState<
    DropdownListGroup | undefined
  >(() => {
    if (!selectedVariable) return undefined;
    const groups = nodeVariableToGroups(variables);
    const group = groups.find((g) => g.groupId === selectedVariable.node_id);
    if (!group) return undefined;
    return group;
  });
  const [variableSearch, setVariableSearch] = useState("");

  useEffect(() => {
    if (!selectedVariable) {
      setSelectedVariable(undefined);
      setSelectedGroup(undefined);
      return;
    }

    const groups = nodeVariableToGroups(variables);
    const foundGroup = groups.find(
      (g) => g.groupId === selectedVariable.node_id,
    );
    const foundVariable = foundGroup?.items.find(
      (i) => i.value === selectedVariable.id,
    );

    if (foundVariable && foundGroup) {
      setSelectedVariable(foundVariable);
      setSelectedGroup(foundGroup);
    }
  }, [selectedVariable, variables]);

  const variableGroups = useMemo(() => {
    const groups = nodeVariableToGroups(variables);
    if (variableSearch) {
      return groups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              (item.label as string)
                .toLowerCase()
                .includes(variableSearch.toLowerCase()) ||
              group.groupLabels.some((label) =>
                label.toLowerCase().includes(variableSearch.toLowerCase()),
              ),
          ),
        }))
        .filter((group) => group.items.length > 0);
    }
    return groups;
  }, [variables, variableSearch]);

  useEffect(() => {
    if (!selectedVariable) {
      setSelectedVariable(undefined);
      setSelectedGroup(undefined);
      return;
    }
    const groups = nodeVariableToGroups(variables);
    const variable = groups
      .find((g) => g.groupId === selectedVariable.node_id)
      ?.items.find((i) => {
        return i.value === selectedVariable.id;
      });
    const group = groups.find((g) => g.groupId === selectedVariable.node_id);
    setSelectedVariable(variable);
    setSelectedGroup(group);
  }, [selectedVariable, variables]);

  return (
    <DropdownList
      options={variableGroups}
      selectedValue={_selectedVariable}
      selectedGroup={_selectedGroup}
      toggleSelection={(variable, group) => {
        setSelectedVariable(variable);
        setSelectedGroup(group);
        const sourceNode = variables.find((v) => v.nodeId === group?.groupId);

        // Find the original variable to preserve all properties (including global_type)
        const originalVariable = sourceNode?.variables.find(
          (v) => v.id === variable.value,
        );

        onSelectVariable(
          originalVariable
            ? {
                ...originalVariable,
                node_id: originalVariable.node_id || group?.groupId || "",
                ref_name:
                  originalVariable.ref_name ||
                  sourceNode?.ref_name ||
                  sourceNode?.type ||
                  "",
              }
            : {
                id: variable.value,
                name: variable.label as string,
                node_id: group?.groupId || "",
                type: variable.detail as
                  | "string"
                  | "number"
                  | "boolean"
                  | "date"
                  | "array",
                ref_name: sourceNode?.ref_name || sourceNode?.type || "",
              },
        );
      }}
      widthClassName={cn("flex-1", className)}
      placeholder={placeholder || "Elegir variable"}
      search={{
        placeholder: "Buscar variable",
        onSearch: (query) => {
          setVariableSearch(query);
        },
        query: variableSearch,
      }}
      group={group}
      disabled={isLoading}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};
